import { createWorker, type Worker } from 'tesseract.js';
// Die Bausteine der Erkennung kommen über den normalen Bau ins Bündel. Vorher wurden
// sie von einem eigenen Skript nach `public/` kopiert — im Container lief das nicht,
// und dann antwortete /tesseract/worker.min.js mit der App-Oberfläche statt mit
// Code. Was Vite selbst ausliefert, kann nicht vergessen werden.
import workerUrl from 'tesseract.js/dist/worker.min.js?url';
import coreSimdUrl from 'tesseract.js-core/tesseract-core-simd-lstm.wasm.js?url';
import corePlainUrl from 'tesseract.js-core/tesseract-core-lstm.wasm.js?url';
import { api } from '../data/api.ts';
import { loadPdfjs } from './extract.ts';

/**
 * Texterkennung auf dem Gerät (docs/KONZEPT.md, Abschnitt 19.4).
 *
 * Tesseract als WASM, deutsches Sprachmodell, alles aus dem eigenen Ursprung — wer
 * ein Dokument erkennt, soll das keinem Dritten mitteilen, auch nicht durch den Abruf
 * des Modells.
 *
 * Gemessen an drei echten Belegen: ein sauberer Scan kommt auf Konfidenz 84 und alle
 * Beträge stimmen; ein Formular auf 65 mit zerfallener Struktur, aber lesbaren
 * Wörtern; ein schräges Handyfoto auf 55. Deshalb wird die Konfidenz mitgeführt und
 * unter `UNSICHER` der bessere Weg angeboten, statt still ein Ergebnis abzulegen.
 */

/** Unterhalb davon gilt das Ergebnis als wacklig. */
export const UNSICHER = 70;

/** Das Sprachmodell liegt im Repo unter `web/public/tesseract`. */
const BASE = '/tesseract';

/**
 * SIMD spart etwa die Hälfte der Rechenzeit, fehlt aber in älteren Browsern. Die
 * Prüfung ist ein winziges WASM-Modul, das nur mit SIMD gültig ist — dieselbe, die
 * `tesseract.js` sonst über eine eigene Abhängigkeit macht.
 */
function simdVerfuegbar(): boolean {
  try {
    return WebAssembly.validate(
      new Uint8Array([
        0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0, 10, 10, 1, 8, 0, 65, 0,
        253, 15, 253, 98, 11,
      ]),
    );
  } catch {
    return false;
  }
}

/** Breite, auf die PDF-Seiten gerechnet werden. Darunter leidet die Erkennung. */
const PAGE_WIDTH = 1600;

export type OcrResult = { text: string; confidence: number; pages: number };
export type Progress = { page: number; pages: number; anteil: number };

let shared: Promise<Worker> | null = null;

/** Die Erkennung ist nicht einsatzbereit — mit einem Grund, den man lesen kann. */
export class OcrUnavailable extends Error {
  constructor(grund: string) {
    super(grund);
    this.name = 'OcrUnavailable';
  }
}

/**
 * Ein Arbeiter für die ganze Sitzung. Das Modell einmal zu laden kostet gut ein
 * Megabyte; für jede Seite neu wäre das absurd.
 *
 * Vorher wird nachgesehen, ob die Bausteine überhaupt ausgeliefert werden. Fehlen
 * sie, bricht `createWorker` mit einer Meldung ab, die niemandem etwas sagt — und
 * ein Knopf, der schweigend nichts tut, ist das Schlimmste, was diese Stelle tun kann.
 */
function engine(): Promise<Worker> {
  if (!shared) {
    shared = (async () => {
      // Das Sprachmodell ist die einzige Datei, die noch aus dem Verzeichnis kommt.
      const antwort = await fetch(`${BASE}/deu.traineddata.gz`, { method: 'HEAD' }).catch(
        () => null,
      );
      if (!antwort?.ok) {
        throw new OcrUnavailable(
          `Das Sprachmodell fehlt: ${BASE}/deu.traineddata.gz antwortet ${
            antwort ? antwort.status : 'gar nicht'
          }.`,
        );
      }
      return createWorker('deu', 1, {
        workerPath: workerUrl,
        // Endet der Pfad auf „js", nimmt tesseract genau diese Datei — die Auswahl
        // nach Fähigkeiten des Geräts machen wir deshalb selbst.
        corePath: simdVerfuegbar() ? coreSimdUrl : corePlainUrl,
        langPath: BASE,
        gzip: true,
      });
    })();
    // Ein gescheiterter Versuch darf den nächsten nicht blockieren.
    shared.catch(() => {
      shared = null;
    });
  }
  return shared;
}

/** Gibt das Modell wieder frei — nach getaner Arbeit hält es sonst Speicher fest. */
export async function releaseEngine(): Promise<void> {
  if (!shared) return;
  const worker = await shared;
  shared = null;
  await worker.terminate();
}

async function recognizeOne(source: Blob): Promise<{ text: string; confidence: number }> {
  const worker = await engine();
  const { data } = await worker.recognize(source);
  return { text: data.text.trim(), confidence: Math.round(data.confidence) };
}

/** Verkleinert ein Bild auf Erkennungsgröße. Ein 12-Megapixel-Foto bringt nichts. */
async function scaled(bytes: Uint8Array, mime: string): Promise<Blob> {
  const source = new Blob([bytes as BlobPart], { type: mime || 'image/jpeg' });
  try {
    const bitmap = await createImageBitmap(source);
    const factor = Math.min(1, PAGE_WIDTH / Math.max(bitmap.width, bitmap.height));
    if (factor === 1) return source;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * factor);
    canvas.height = Math.round(bitmap.height * factor);
    const context = canvas.getContext('2d');
    if (!context) return source;
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.85),
    );
    return blob ?? source;
  } catch {
    return source;
  }
}

function toBase64(bytes: Uint8Array): string {
  let binary = '';
  // In Häppchen: `String.fromCharCode(...alles)` sprengt bei großen Bildern den Stapel.
  for (let start = 0; start < bytes.length; start += 8192) {
    binary += String.fromCharCode(...bytes.subarray(start, start + 8192));
  }
  return btoa(binary);
}

/**
 * Der zweite Weg: dieselben Seitenbilder, aber über den eigenen Server an Claude.
 * Wird ausschließlich auf ausdrücklichen Wunsch gerufen — hier verlässt das Dokument
 * das Gerät.
 */
export async function recognizeRemote(
  bytes: Uint8Array,
  mime: string,
  name: string,
  options: { maxPages?: number; onProgress?: (progress: Progress) => void } = {},
): Promise<OcrResult> {
  const limit = options.maxPages ?? 20;
  const isPdf = mime === 'application/pdf' || name.toLowerCase().endsWith('.pdf');
  const sources = isPdf ? await pdfPages(bytes, limit) : [await scaled(bytes, mime)];

  const texts: string[] = [];
  for (const [index, source] of sources.entries()) {
    options.onProgress?.({ page: index + 1, pages: sources.length, anteil: index / sources.length });
    const data = toBase64(new Uint8Array(await source.arrayBuffer()));
    const { text } = await api.ocr(data, source.type || 'image/jpeg');
    texts.push(text);
  }
  options.onProgress?.({ page: sources.length, pages: sources.length, anteil: 1 });

  // Claude nennt keine Konfidenz. Das ist ehrlicher als eine erfundene Zahl.
  return { text: texts.join('\n\n').trim(), confidence: 0, pages: sources.length };
}

/** Rechnet die Seiten eines PDFs in Bilder — Tesseract sieht nur Pixel. */
async function pdfPages(bytes: Uint8Array, limit: number): Promise<Blob[]> {
  const pdfjs = await loadPdfjs();
  // Nicht `document` nennen: Das ist hier das DOM, und die Leinwand kommt von dort.
  // `bytes.slice()`: pdfjs übergibt die Daten an seinen Arbeiter und **entkoppelt
  // dabei den Speicher** — das Original ist danach unbrauchbar. Wer es anschließend
  // noch hochladen will, bekommt „detached ArrayBuffer" um die Ohren. Genau daran
  // sind PDF-Uploads gescheitert, seit der Arbeiter richtig eingerichtet ist.
  const pdf = await pdfjs.getDocument({ data: bytes.slice() }).promise;
  const pages: Blob[] = [];

  for (let index = 1; index <= Math.min(pdf.numPages, limit); index += 1) {
    const page = await pdf.getPage(index);
    const base = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: PAGE_WIDTH / base.width });
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(viewport.width);
    canvas.height = Math.round(viewport.height);
    const context = canvas.getContext('2d');
    if (!context) break;
    await page.render({ canvas, canvasContext: context, viewport } as never).promise;
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    if (blob) pages.push(blob);
    canvas.width = 0;
    canvas.height = 0;
  }
  await pdf.cleanup();
  return pages;
}

/** Wie viele Seiten ein PDF hat, ohne es zu erkennen — für die Rückfrage vorher. */
export async function pageCount(bytes: Uint8Array, mime: string, name: string): Promise<number> {
  if (!(mime === 'application/pdf' || name.toLowerCase().endsWith('.pdf'))) return 1;
  try {
    const pdfjs = await loadPdfjs();
    // `bytes.slice()`: pdfjs übergibt die Daten an seinen Arbeiter und **entkoppelt
  // dabei den Speicher** — das Original ist danach unbrauchbar. Wer es anschließend
  // noch hochladen will, bekommt „detached ArrayBuffer" um die Ohren. Genau daran
  // sind PDF-Uploads gescheitert, seit der Arbeiter richtig eingerichtet ist.
  const pdf = await pdfjs.getDocument({ data: bytes.slice() }).promise;
    const count = pdf.numPages;
    await pdf.cleanup();
    return count;
  } catch {
    return 1;
  }
}

/**
 * Erkennt eine Datei. Mehrseitige PDFs werden Seite für Seite gerechnet; die
 * Konfidenz ist der Durchschnitt, gewichtet nach Textmenge — eine fast leere Seite
 * soll das Urteil über die anderen nicht verderben.
 */
export async function recognize(
  bytes: Uint8Array,
  mime: string,
  name: string,
  options: { maxPages?: number; onProgress?: (progress: Progress) => void } = {},
): Promise<OcrResult> {
  const limit = options.maxPages ?? 50;
  const isPdf = mime === 'application/pdf' || name.toLowerCase().endsWith('.pdf');
  const sources = isPdf
    ? await pdfPages(bytes, limit)
    : [new Blob([bytes as BlobPart], { type: mime || 'image/jpeg' })];

  const texts: string[] = [];
  let weighted = 0;
  let weight = 0;

  for (const [index, source] of sources.entries()) {
    options.onProgress?.({ page: index + 1, pages: sources.length, anteil: index / sources.length });
    const result = await recognizeOne(source);
    texts.push(result.text);
    weighted += result.confidence * Math.max(1, result.text.length);
    weight += Math.max(1, result.text.length);
  }
  options.onProgress?.({ page: sources.length, pages: sources.length, anteil: 1 });

  return {
    text: texts.join('\n\n').trim(),
    confidence: weight > 0 ? Math.round(weighted / weight) : 0,
    pages: sources.length,
  };
}
